import {
	Box,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	ThemeProvider,
	createTheme,
} from '@mui/material';
import stripe from 'assets/stripe.png';

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from 'utils/axios';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useSelector } from 'react-redux';
import Loader from 'component/loader';
import SocketManager from '../../../services/SocketManager';

const theme = createTheme({
	breakpoints: {
		values: {
			xs: 0,
			sm: 320,
			md: 600,
			lg: 960,
			xl: 1280,
		},
	},
});

const DocumentTranslationHistory = () => {
	const [loading, setLoading] = useState(true);
	const [documentDetail, setDocumentDetail] = useState([]);
	const { profileInformation } = useSelector((state) => state.user);

	useEffect(() => {
		if (profileInformation?._id) {
			api
				.get(`/history/document-translation/${profileInformation?._id}`)
				.then((res) => {
					setLoading(true);
					const filterDetails = res?.data
						?.filter((el) => {
							return el?.originalContent !== 'null' || el?.status;
						})
						.reverse();

					setDocumentDetail(filterDetails);
					setLoading(false);
				})
				.catch((error) => {
					setLoading(false);
					console.log(error);
				});
		}
	}, [profileInformation]);

	useEffect(() => {
		if (profileInformation?._id) {
			SocketManager.emit('join_translation_room', profileInformation._id);

			const handleTranslationProgress = (data) => {
				if (data?.translationId) {
					setDocumentDetail((prevDetails) =>
						prevDetails.map((item) => {
							if (
								item._id === data.translationId &&
								item.status === 'Progress'
							) {
								return {
									...item,
									processingPercentage: data.percentage || 0,
								};
							}
							return item;
						})
					);
				}
			};

			const handleTranslationFinished = (data) => {
				if (data?.translationId) {
					setDocumentDetail((prevDetails) =>
						prevDetails.map((item) => {
							if (
								item._id === data.translationId &&
								item.status === 'Progress'
							) {
								return {
									...item,
									processingPercentage: data.percentage || 100,
								};
							}
							return item;
						})
					);
				}
			};

			const handleTranslationReady = (data) => {
				if (data?.translationId && profileInformation?._id) {
					api
						.get(`/history/document-translation/${profileInformation._id}`)
						.then((res) => {
							const filterDetails = res?.data
								?.filter((el) => {
									return el?.originalContent !== 'null' || el?.status;
								})
								.reverse();

							setDocumentDetail(filterDetails);
						})
						.catch((error) => {
							console.log('Error fetching updated translation data:', error);
						});
				}
			};

			SocketManager.on('translation-progress', handleTranslationProgress);
			SocketManager.on('translation-finished', handleTranslationFinished);
			SocketManager.on('translation-ready', handleTranslationReady);

			return () => {
				SocketManager.off('translation-progress', handleTranslationProgress);
				SocketManager.off('translation-finished', handleTranslationFinished);
				SocketManager.off('translation-ready', handleTranslationReady);
			};
		}
	}, [profileInformation?._id]);

	return (
		<>
			{loading && <Loader />}
			<Box>
				<Container>
					<Box className="bg-white h-full rounded-lg rounded-br-lg shadow-lg">
						<div className="flex flex-col gap-5 p-8">
							<p className="mb-5 xm:text-2xl font-[600] xs:text-sm">
								Document Translation History
							</p>
							<ThemeProvider theme={theme}>
								<TableContainer component={Paper}>
									<Table sx={{ minWidth: 650 }} aria-label="simple table">
										<TableHead>
											<TableRow>
												<TableCell
													sx={{
														paddingLeft: { xs: 2, md: 5, lg: 7, xl: 10 },
													}}
												>
													Id
												</TableCell>
												<TableCell align="left">Words count</TableCell>
												<TableCell align="left">Translate at</TableCell>
												<TableCell align="left">Status</TableCell>
												<TableCell align="left">Progress</TableCell>
												<TableCell align="left">Download</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{documentDetail?.map((documentDetail) => (
												<TableRow
													key={documentDetail._id}
													sx={{
														'&:last-child td, &:last-child th': { border: 0 },
													}}
												>
													<TableCell
														component="th"
														scope="row"
														sx={{
															paddingLeft: { xs: 2, md: 5, lg: 7, xl: 10 },
														}}
													>
														{documentDetail?._id}
													</TableCell>
													<TableCell align="left">
														{documentDetail?.translatedWords}
													</TableCell>
													<TableCell align="left">
														{documentDetail?.createdAt}
													</TableCell>
													<TableCell align="left">
														{documentDetail?.status === 'Progress' && 'In '}
														{documentDetail?.status}
													</TableCell>
													<TableCell align="left">
														{documentDetail?.status === 'Progress' &&
														documentDetail?.processingPercentage !==
															undefined ? (
															<span
																style={{
																	fontWeight: '600',
																	color: '#6db7f2',
																}}
															>
																{Math.floor(
																	documentDetail.processingPercentage || 0
																)}
																%
															</span>
														) : (
															<span style={{ color: '#999' }}>-</span>
														)}
													</TableCell>
													<TableCell align="left">
														{documentDetail?.status === 'Completed' ? (
															<a
																target="_blank"
																rel="noreferrer"
																href={documentDetail?.originalContent}
															>
																<CloudDownloadIcon
																	style={{
																		cursor: 'pointer',
																		color: '#6db7f2',
																	}}
																/>
															</a>
														) : (
															<CloudDownloadIcon
																style={{
																	cursor: 'not-allowed',
																	color: '#cccccc',
																}}
															/>
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</ThemeProvider>
						</div>
						<div className="text-center pt-10">
							<p className="text-lg text-[#00000099]">
								Secure payment transctions are facilitated through
							</p>
							<div className="flex justify-center items-center">
								<img src={stripe} alt="stripe" width={150} />
							</div>
						</div>
					</Box>
				</Container>
			</Box>
		</>
	);
};

export default DocumentTranslationHistory;
